import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { enableFabricEmbedMode, isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import EditorWrapper from '@/routes/GraphEditor/EditorWrapper'
import { EDITOR_MODE, GRAPH_PREVIEW } from '@/routes/GraphEditor'

// View modes the user can switch between inside the Fabric embed. JSON view is
// intentionally excluded here — it is an internal-org debugging tool and must
// not be exposed in the embedded experience.
const EMBED_TABS = [EDITOR_MODE, GRAPH_PREVIEW] as const

/**
 * Embed-friendly route for viewing P&ID graphs in a Fabric iframe.
 * Auth is handled via PostMessage token exchange (not Keycloak).
 * Renders EditorWrapper in read-only mode.
 *
 * The user can toggle between Editor Mode and Graph Preview via the segmented
 * control overlaid on the canvas. JSON view is deliberately not offered in the
 * embed (internal-org debugging tool only).
 *
 * Security: This route is embedded from the DataCatalog item's DocumentDetailView,
 * which sends AUTH_TOKEN via PostMessage. The allowedDocumentIds mechanism (used by
 * PdfViewer inside Board embeds) does not apply here — the DataCatalog host only
 * embeds graphs that belong to the user's own catalog, and API-level auth (the
 * exchanged token) protects data access.
 */
export default function GraphEmbedView() {
  const { graph_id } = useParams<{ graph_id: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<string>(EDITOR_MODE)

  // Fabric embed mode: detect from URL query parameter
  const isEmbedMode = searchParams.get('embed') === 'fabric'
  useEffect(() => {
    if (isEmbedMode && !isFabricEmbedMode()) {
      enableFabricEmbedMode()
    }
  }, [isEmbedMode])

  const getTabLabel = (tabKey: string) => {
    switch (tabKey) {
      case EDITOR_MODE:
        return t('data_catalog:graph_editor.tabs.editor', { defaultValue: 'Editor Mode' })
      case GRAPH_PREVIEW:
        return t('data_catalog:graph_editor.tabs.preview', { defaultValue: 'Graph Preview' })
      default:
        return tabKey
    }
  }

  if (!graph_id) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-dark-muted">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-2">Graph Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400">No graph ID was provided.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* View-mode selector — lets the user switch between Editor Mode and
          Graph Preview within the embed. Overlaid on the canvas since the embed
          intentionally omits the full GraphEditor navbar. */}
      <div
        data-testid="embed-view-mode-selector"
        className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-[#f5f5f5] dark:bg-dark-accent flex items-center p-[3px] rounded-lg h-8 shadow-sm"
      >
        {EMBED_TABS.map((tabKey) => {
          const isActive = activeTab === tabKey
          return (
            <button
              key={tabKey}
              type="button"
              data-testid={`embed-tab-${tabKey}`}
              onClick={() => setActiveTab(tabKey)}
              className={`px-3 text-sm font-medium rounded-md transition-all h-full flex items-center ${
                isActive
                  ? 'bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {getTabLabel(tabKey)}
            </button>
          )
        })}
      </div>

      <EditorWrapper
        graphId={graph_id}
        isReadOnly={true}
        activeTab={activeTab}
        initialZoom={1}
      />
    </div>
  )
}
